import React, { useState, useCallback, useEffect, useRef, forwardRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { 
  Upload, X, CheckCircle, Phone, ArrowRight, Loader2
} from 'lucide-react';
import { trackEvent, trackEventOnce } from '../utils/analytics';
import { GoogleLogo } from './GoogleLogo';
import { formatFileSizeMb, MAX_UPLOAD_TOTAL_MB, validateMediaFiles } from '../utils/mediaUpload';
import { AUSTRALIAN_STATES } from '../utils/australianStates';
import { useTodayDate } from '../utils/useTodayDate';

const InlineBookingForm = forwardRef(({ prefill, initialPrinterBrand = '', formTitle = 'Get a FREE repair quote', formSubtitle = 'Free quote. No payment required.', pageSource = 'Homepage' }, ref) => {
  const [expanded, setExpanded] = useState(() => Boolean((prefill?.sequence || prefill?.autoApply) && prefill?.expand !== false));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const today = useTodayDate();
  const [formError, setFormError] = useState('');
  const hasTrackedStart = useRef(false);
  const hasTrackedExpand = useRef(false);

  const [formData, setFormData] = useState(() => ({
    serviceType: '',
    printerBrand: prefill?.printerBrand || initialPrinterBrand,
    issueDescription: prefill?.issueDescription || '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    customerState: '',
    organization: '',
    printerModel: '',
    multipleMachines: false,
    machinesList: '',
    urgency: prefill?.urgency || '',
    serviceAddress: '',
    preferredDate: '',
    preferredContact: 'email',
    ...(prefill?.serviceType ? { serviceType: prefill.serviceType } : {}),
    agreeToTerms: false
  }));

  useEffect(() => {
    trackEventOnce('hero_booking_form_view', 'booking_form_view', { form_location: 'hero' });
  }, [trackEventOnce]);

  const trackFormStart = useCallback((field) => {
    if (hasTrackedStart.current) return;
    hasTrackedStart.current = true;
    trackEvent('booking_form_start', {
      form_location: 'hero',
      first_field: field
    });
  }, [trackEvent]);

  const trackFormExpand = useCallback((trigger) => {
    if (hasTrackedExpand.current) return;
    hasTrackedExpand.current = true;
    trackEvent('booking_form_step_expand', {
      form_location: 'hero',
      trigger
    });
  }, [trackEvent]);

  const handleInputChange = useCallback((field, value) => {
    trackFormStart(field);
    setFormData(prev => ({ ...prev, [field]: value }));
    setFormError('');
    if (!expanded) {
      trackFormExpand(field);
      setExpanded(true);
    }
  }, [expanded, setExpanded, setFormData, setFormError, trackFormExpand, trackFormStart]);

  const handleExpand = useCallback(() => {
    trackFormStart('focus');
    if (!expanded) {
      trackFormExpand('focus');
      setExpanded(true);
    }
  }, [expanded, trackFormExpand, trackFormStart]);

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    const validationMessage = validateMediaFiles(uploadedFiles, files);
    if (validationMessage) {
      setFormError(validationMessage);
      event.target.value = '';
      return;
    }
    const newFiles = files.map(file => ({
      file,
      name: file.name,
      size: formatFileSizeMb(file.size),
      id: Math.random().toString(36).substr(2, 9)
    }));
    setUploadedFiles(prev => [...prev, ...newFiles]);
    setFormError('');
    event.target.value = '';
  };

  const removeFile = (fileId) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    trackEvent('booking_form_submit_attempt', {
      form_location: 'hero',
      service_type: formData.serviceType || 'not_selected',
      urgency: formData.urgency || 'not_selected',
      printer_brand: formData.printerBrand || 'not_selected'
    });
    const requiredSelections = [
      ['printerBrand', 'Please select your printer brand.'],
      ['issueDescription', 'Please describe the printer issue.'],
      ['urgency', 'Please choose how urgent the repair is.'],
      ['serviceType', 'Please choose the repair path that best fits.'],
      ['customerState', 'Please select what state you are located in.']
    ];
    const missing = requiredSelections.find(([field]) => !formData[field]);
    if (missing) {
      setExpanded(true);
      setFormError(missing[1]);
      trackEvent('booking_form_submit_error', {
        form_location: 'hero',
        error_type: 'validation_missing_required',
        missing_field: missing[0]
      });
      return;
    }
    setIsSubmitting(true);
    setFormError('');
    try {
      const formDataToSubmit = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key] !== '' && formData[key] !== false) {
          formDataToSubmit.append(key, formData[key]);
        }
      });
      uploadedFiles.forEach(file => {
        formDataToSubmit.append('images', file.file);
      });
      formDataToSubmit.append('pageSource', pageSource);
      formDataToSubmit.append('pagePath', window.location.pathname);
      formDataToSubmit.append('state', formData.customerState);
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/book-service`, {
        method: 'POST',
        body: formDataToSubmit
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      trackEvent('booking_form_submit_success', {
        form_location: 'hero',
        service_type: formData.serviceType,
        urgency: formData.urgency,
        printer_brand: formData.printerBrand
      });
      setIsSubmitted(true);
    } catch {
      setFormError('There was an error submitting your request. Please try again or call 1300 334 327.');
      trackEvent('booking_form_submit_error', {
        form_location: 'hero',
        error_type: 'api_or_network_error',
        service_type: formData.serviceType || 'not_selected',
        urgency: formData.urgency || 'not_selected',
        printer_brand: formData.printerBrand || 'not_selected'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setIsSubmitted(false);
    setExpanded(false);
    setFormData({
      serviceType: '', printerBrand: '', issueDescription: '',
      firstName: '', lastName: '', email: '', phone: '', customerState: '',
      organization: '', printerModel: '', multipleMachines: false,
      machinesList: '', urgency: '', serviceAddress: '',
      preferredDate: '', preferredContact: 'email', agreeToTerms: false
    });
    setUploadedFiles([]);
    setFormError('');
  };

  if (isSubmitted) {
    return (
      <div ref={ref} data-testid="inline-booking-success" className="bg-white rounded-sm border border-gray-200 overflow-hidden">
        <div className="p-6 sm:p-8 text-center">
          <div className="w-12 h-12 bg-gray-100 rounded-sm flex items-center justify-center mx-auto mb-4" data-testid="booking-success-icon-wrap">
            <CheckCircle className="w-6 h-6 text-green-600" />
          </div>
          <h3 className="text-lg font-bold text-gray-900 mb-2" data-testid="booking-success-title">Request submitted</h3>
          <p className="text-sm text-gray-600 mb-5" data-testid="booking-success-message">
            We&apos;ll contact you within 24 hours with a free quote.
          </p>
          <div className="bg-gray-50 border border-gray-200 rounded-sm p-3.5 mb-5 text-left" data-testid="booking-success-next-steps">
            <ul className="text-gray-700 text-sm space-y-1.5">
              <li className="flex items-start gap-2"><span className="text-gray-400">1.</span> Our team reviews your request</li>
              <li className="flex items-start gap-2"><span className="text-gray-400">2.</span> Transparent quote before any work</li>
              <li className="flex items-start gap-2"><span className="text-gray-400">3.</span> Schedule your service appointment</li>
            </ul>
          </div>
          <Button onClick={resetForm} variant="outline" className="border-gray-300 text-gray-700 hover:bg-gray-50 w-full rounded-sm" data-testid="submit-another-request-button">
            Submit another request
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div ref={ref} data-testid="inline-booking-form" className="bg-white rounded-sm border border-gray-200 overflow-hidden transition-[border-color,outline-color] duration-200">
      <div className="h-2 bg-orange-500" data-testid="booking-form-cta-accent" />
      <div className="bg-gray-900 px-5 py-4 sm:px-6 sm:py-5">
        <p className="text-[11px] font-extrabold uppercase tracking-[0.16em] text-orange-400 mb-1" data-testid="booking-form-eyebrow">Start here</p>
        <h3 className="text-lg sm:text-xl font-extrabold text-white leading-tight" data-testid="booking-form-title">{formTitle}</h3>
        <p className="text-gray-300 text-xs mt-1" data-testid="booking-form-subtitle">{formSubtitle}</p>
      </div>

      <ul className="border-b border-gray-200 bg-gray-50 px-5 py-3 space-y-1.5 list-disc list-inside text-xs text-gray-600" data-testid="booking-form-trust-list">
        <li data-testid="booking-form-trust-item-free-quote">Free quote</li>
        <li data-testid="booking-form-trust-item-response">24hr response</li>
        <li className="font-semibold text-gray-700" data-testid="booking-form-trust-item-rating"><span className="inline-flex items-center gap-1.5" data-testid="booking-form-google-rating"><GoogleLogo className="h-3.5 w-3.5 flex-shrink-0" testId="booking-form-google-logo" />4.9/5 (700+ reviews)</span></li>
      </ul>
      <a
        href="tel:1300334327"
        onClick={() => trackEvent('phone_click', { phone_location: 'hero_form' })}
        className="flex items-center justify-center gap-1.5 border-b border-gray-100 px-4 py-2 text-xs font-semibold text-gray-700 hover:text-gray-900 hover:bg-gray-50 transition-colors"
        data-testid="booking-form-phone-link"
      >
        <Phone className="h-3.5 w-3.5 text-orange-500" />
        <span data-testid="booking-form-phone-text">Prefer to speak to someone? Call 1300 334 327 — Option 2 for repairs</span>
      </a>

      <form onSubmit={handleSubmit}>
        <div className="px-5 py-5 sm:px-6 space-y-4">
          {formError && (
            <div className="border border-red-200 bg-red-50 text-red-700 text-xs font-semibold px-3 py-2 rounded-sm" data-testid="booking-form-error">
              {formError}
            </div>
          )}

          {/* Step 1: Hook fields — always visible */}
          <div className="space-y-3.5" data-testid="booking-step-1">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-gray-700">Printer Brand *</Label>
              <Select onValueChange={(value) => handleInputChange('printerBrand', value)} value={formData.printerBrand}>
                <SelectTrigger data-testid="brand-select" className="h-10 rounded-sm" onFocus={handleExpand}>
                  <SelectValue placeholder="Select your printer brand" />
                </SelectTrigger>
                <SelectContent>
                  {['Bambu Lab','Prusa','Creality','Ultimaker','MakerBot','Raise3D','FlashForge','Anycubic','Qidi Tech','Elegoo','Snapmaker','Artillery','Voron','Sovol','Formlabs','Other'].map(b => (
                    <SelectItem key={b} value={b.toLowerCase().replace(/\s+/g,'-')} data-testid={`brand-option-${b.toLowerCase().replace(/\s+/g,'-')}`}>{b}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-gray-700">What&apos;s the problem? *</Label>
              <Textarea
                data-testid="issue-description"
                value={formData.issueDescription}
                onChange={(e) => handleInputChange('issueDescription', e.target.value)}
                onFocus={handleExpand}
                placeholder="e.g. Nozzle keeps clogging, bed won&apos;t heat up..."
                rows={2}
                className="resize-none text-sm rounded-sm"
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-gray-700">How urgent is it? *</Label>
              <Select onValueChange={(value) => handleInputChange('urgency', value)} value={formData.urgency}>
                <SelectTrigger data-testid="urgency-select" className="h-10 text-sm rounded-sm" onFocus={handleExpand}>
                  <SelectValue placeholder="Select urgency" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard" data-testid="urgency-option-standard">Standard (3-5 days)</SelectItem>
                  <SelectItem value="priority" data-testid="urgency-option-priority">Priority (1-2 days)</SelectItem>
                  <SelectItem value="same-day" data-testid="urgency-option-same-day">Same Day</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* No fabricated live enquiry counts. */}
            {!expanded && (
              <p className="text-xs text-gray-600 text-center pt-1" data-testid="booking-quote-reassurance">
                No payment required to request a quote
              </p>
            )}
          </div>

          {/* Step 2: Expanded — auto-reveals on interaction */}
          <div
            data-testid="booking-step-2"
            className={`space-y-4 transition-all duration-500 ease-out overflow-hidden ${
              expanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
            }`}
          >
            {/* Contact details */}
            <div className="border-t border-gray-100 pt-4">
              <p className="text-xs font-semibold text-gray-700 mb-2.5">Repair path</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 mb-4">
                {[
                  { value: 'in-store', label: 'In-Store Repair', sub: 'Best for most faults' },
                  { value: 'on-site', label: 'On-Site Service', sub: 'Schools and fleets' },
                  { value: 'maintenance', label: 'Maintenance', sub: 'Prevent future failures' }
                ].map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    data-testid={`service-type-${opt.value}`}
                    onClick={() => handleInputChange('serviceType', opt.value)}
                    className={`text-left rounded-sm px-3 py-2.5 border transition-colors duration-150 ${
                      formData.serviceType === opt.value
                        ? 'border-gray-900 bg-gray-50 ring-1 ring-gray-300'
                        : 'border-gray-200 hover:border-gray-900 bg-white'
                    }`}
                  >
                    <span className="block text-xs font-semibold text-gray-900">{opt.label}</span>
                    <span className="block text-[10px] text-gray-500 mt-0.5">{opt.sub}</span>
                  </button>
                ))}
              </div>
              <p className="text-xs font-semibold text-gray-700 mb-2.5 flex items-center gap-1.5">
                <Phone className="h-3.5 w-3.5 text-gray-900" />
                Your details
              </p>
              <div className="grid grid-cols-2 gap-2.5">
                <div className="space-y-1">
                  <Label className="text-[11px] text-gray-500">First Name *</Label>
                  <Input data-testid="first-name" value={formData.firstName} onChange={(e) => handleInputChange('firstName', e.target.value)} required className="h-9 text-sm rounded-sm" />
                </div>
                <div className="space-y-1">
                  <Label className="text-[11px] text-gray-500">Last Name *</Label>
                  <Input data-testid="last-name" value={formData.lastName} onChange={(e) => handleInputChange('lastName', e.target.value)} required className="h-9 text-sm rounded-sm" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2.5 mt-2.5">
                <div className="space-y-1">
                  <Label className="text-[11px] text-gray-500">Email *</Label>
                  <Input data-testid="email" type="email" value={formData.email} onChange={(e) => handleInputChange('email', e.target.value)} required className="h-9 text-sm rounded-sm" />
                </div>
                <div className="space-y-1">
                  <Label className="text-[11px] text-gray-500">Phone *</Label>
                  <Input data-testid="phone" type="tel" value={formData.phone} onChange={(e) => handleInputChange('phone', e.target.value)} required className="h-9 text-sm rounded-sm" />
                </div>
              </div>
              <div className="mt-2.5 space-y-1">
                <Label className="text-[11px] text-gray-500">What state are you located in? *</Label>
                <Select onValueChange={(value) => handleInputChange('customerState', value)} value={formData.customerState}>
                  <SelectTrigger data-testid="inline-customer-state-select" className="h-9 text-sm rounded-sm">
                    <SelectValue placeholder="Select state" />
                  </SelectTrigger>
                  <SelectContent>
                    {AUSTRALIAN_STATES.map((state) => (
                      <SelectItem key={state.value} value={state.value} data-testid={`inline-customer-state-option-${state.value.toLowerCase()}`}>{state.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="mt-2.5 space-y-1">
                <Label className="text-[11px] text-gray-500">Organization / School (optional)</Label>
                <Input data-testid="organization" value={formData.organization} onChange={(e) => handleInputChange('organization', e.target.value)} placeholder="School, business, etc." className="h-9 text-sm rounded-sm" />
              </div>
            </div>

            {/* Printer details */}
            <div className="border-t border-gray-100 pt-4 space-y-2.5">
              <p className="text-xs font-semibold text-gray-700 mb-1">Printer details</p>
              <div className="grid grid-cols-2 gap-2.5">
                <div className="space-y-1">
                  <Label className="text-[11px] text-gray-500">Model *</Label>
                  <Input data-testid="printer-model" value={formData.printerModel} onChange={(e) => handleInputChange('printerModel', e.target.value)} placeholder="e.g. X1 Carbon" required className="h-9 text-sm rounded-sm" />
                </div>
                <div className="space-y-1">
                  <Label className="text-[11px] text-gray-500">Preferred contact</Label>
                  <Select onValueChange={(value) => handleInputChange('preferredContact', value)} value={formData.preferredContact}>
                    <SelectTrigger data-testid="preferred-contact-select" className="h-9 text-sm rounded-sm">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="email" data-testid="preferred-contact-option-email">Email</SelectItem>
                      <SelectItem value="phone" data-testid="preferred-contact-option-phone">Phone</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox id="inline-multipleMachines" data-testid="multiple-machines" checked={formData.multipleMachines} onCheckedChange={(checked) => handleInputChange('multipleMachines', checked)} />
                <Label htmlFor="inline-multipleMachines" className="text-xs text-gray-600">Multiple machines need service</Label>
              </div>
              {formData.multipleMachines && (
                <Textarea data-testid="machines-list" value={formData.machinesList} onChange={(e) => handleInputChange('machinesList', e.target.value)} placeholder="List each machine: brand and model" rows={2} className="resize-none text-sm rounded-sm" />
              )}
            </div>

            {/* On-site conditional fields */}
            {formData.serviceType === 'on-site' && (
              <div className="border-t border-gray-100 pt-4 space-y-2.5">
                <p className="text-xs font-semibold text-gray-700 mb-1">On-site details</p>
                <div className="space-y-1">
                  <Label className="text-[11px] text-gray-500">Service Address *</Label>
                  <Textarea data-testid="service-address" value={formData.serviceAddress} onChange={(e) => handleInputChange('serviceAddress', e.target.value)} placeholder="Full address incl. suburb, state, postcode" rows={2} required className="resize-none text-sm rounded-sm" />
                </div>
                <div className="space-y-1">
                  <Label className="text-[11px] text-gray-500">Preferred Date *</Label>
                  <Input data-testid="preferred-date" type="date" value={formData.preferredDate} onChange={(e) => handleInputChange('preferredDate', e.target.value)} min={today} required className="h-9 text-sm rounded-sm" />
                </div>
              </div>
            )}

            {/* Photo/video upload */}
            <div className="border-t border-gray-100 pt-4">
              <Label className="text-xs font-semibold text-gray-700">Photos or videos (optional)</Label>
              <p className="text-[11px] text-gray-500 mb-1.5">Short clips and photos help us diagnose faster. {MAX_UPLOAD_TOTAL_MB}MB total.</p>
              <div className="border border-dashed border-gray-200 rounded-sm p-3 text-center hover:border-gray-900 transition-colors">
                <input data-testid="photo-upload-input" type="file" id="inlineFileUpload" multiple accept="image/*,video/*" onChange={handleFileUpload} className="hidden" />
                <label data-testid="photo-upload-label" htmlFor="inlineFileUpload" className="cursor-pointer flex flex-col items-center gap-0.5">
                  <Upload className="h-5 w-5 text-gray-400" />
                  <span className="text-xs text-gray-500">Click to upload photos/videos</span>
                </label>
              </div>
              {uploadedFiles.length > 0 && (
                <div className="mt-1.5 space-y-1">
                  {uploadedFiles.map(file => (
                    <div key={file.id} className="flex items-center justify-between p-1.5 bg-gray-50 rounded-sm text-xs" data-testid={`uploaded-file-${file.id}`}>
                      <span className="text-gray-700 truncate">{file.name}</span>
                      <button data-testid={`remove-uploaded-file-${file.id}`} type="button" onClick={() => removeFile(file.id)} className="text-gray-400 hover:text-red-500 ml-2">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Terms + Submit */}
            <div className="border-t border-gray-100 pt-4">
              <div className="flex items-start space-x-2 mb-3">
                <Checkbox id="inline-agreeToTerms" data-testid="agree-terms" checked={formData.agreeToTerms} onCheckedChange={(checked) => handleInputChange('agreeToTerms', checked)} />
                <Label htmlFor="inline-agreeToTerms" className="text-[11px] text-gray-500 leading-relaxed">
                  I agree to be contacted by 3D Printer Gear regarding this service request. A quote will be provided before any work begins.
                </Label>
              </div>
              <Button
                type="submit"
                data-testid="submit-booking-btn"
                disabled={!formData.agreeToTerms || isSubmitting}
                className="w-full bg-gray-900 hover:bg-black text-white h-11 text-sm font-semibold rounded-sm border-b-4 border-orange-500 hover:border-orange-400 transition-colors"
              >
                {isSubmitting ? (
                  <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Submitting...</>
                ) : (
                  <>Submit Service Request <ArrowRight className="ml-2 h-4 w-4" /></>
                )}
              </Button>
              <p className="text-[11px] text-gray-400 text-center mt-2">
                No payment required. Free quote within 24hrs.
              </p>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
});

InlineBookingForm.displayName = 'InlineBookingForm';

export default InlineBookingForm;
